import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { SorterResult, TablePaginationConfig } from "antd/es/table/interface";
import { useIntl } from "react-intl";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import {
  EntitiesSupportingHistory,
  EntityTypeSupportingHistory,
} from "@mapmycustomers/shared/types/entity";
import { EntityHistoryRowWithRecord } from "@mapmycustomers/shared/types/entity/EntityHistoryRow";

import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";

import Table from "../../Table";
import useView from "../../view/utils/useView";
import { getEntityHistory, getEntityHistoryPageCount, isEntityHistoryLoading } from "../store";
import { fetchHistory } from "../store/actions";
import useColumns from "../util/useColumns";

const getUniqueKey = (record: EntityHistoryRowWithRecord): string =>
  `${record.updatedAt}_${record.actionType}_${record.fieldName}`;

interface TableState {
  fieldName: string;
  order: SortOrder;
  page: number;
}

interface Props {
  entity: EntitiesSupportingHistory;
  entityType: EntityTypeSupportingHistory;
  fetchHistory: typeof fetchHistory.request;
  history: EntityHistoryRowWithRecord[];
  loading: boolean;
  pageCount: number;
  showEntityView: typeof showEntityView;
}

const HistoryTable: React.FC<Props> = ({
  entity,
  entityType,
  fetchHistory,
  history,
  loading,
  pageCount,
  showEntityView,
}) => {
  const intl = useIntl();
  const {
    viewState: { filter },
  } = useView();
  const [tableState, setTableState] = useState<TableState>({
    fieldName: "updatedAt",
    order: SortOrder.DESC,
    page: 1,
  });

  const columns = useColumns(
    intl,
    entityType,
    entity,
    tableState.fieldName,
    tableState.order,
    showEntityView
  );
  useEffect(() => {
    fetchHistory({
      entityId: entity.id,
      entityType,
      filter,
      page: tableState.page,
      sortFieldName: tableState.fieldName,
      sortOrder: tableState.order,
    });
  }, [entity.id, entityType, fetchHistory, filter, tableState]);

  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      _,
      sorting: SorterResult<EntityHistoryRowWithRecord> | SorterResult<EntityHistoryRowWithRecord>[]
    ) => {
      const stateToUpdate = { ...tableState, page: pagination.current ?? 1 };

      if (!Array.isArray(sorting) && sorting.columnKey) {
        const fieldName: string | undefined = (sorting as SorterResult<EntityHistoryRowWithRecord>)
          .columnKey as string;
        const order = sorting.order === "descend" ? SortOrder.DESC : SortOrder.ASC;
        stateToUpdate.fieldName = fieldName;
        stateToUpdate.order = order;
        if (fieldName !== tableState.fieldName) {
          stateToUpdate.page = 1;
        }
      }
      setTableState(stateToUpdate);
    },
    [setTableState, tableState]
  );

  return (
    <Table<EntityHistoryRowWithRecord>
      columns={columns}
      dataSource={history}
      loading={loading}
      noHover
      onChange={handleTableChange}
      pagination={{
        current: tableState.page,
        pageSize: history.length,
        position: ["bottomCenter"],
        showSizeChanger: false,
        total: pageCount * history.length,
      }}
      rowKey={getUniqueKey}
      scrollable
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  history: getEntityHistory(state),
  loading: isEntityHistoryLoading(state),
  pageCount: getEntityHistoryPageCount(state),
});

const mapDispatchToProps = {
  fetchHistory: fetchHistory.request,
  showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(HistoryTable);
